import {
  onboardingCategories,
  onboardingTopicContentTypes,
  onboardingWalkthroughs,
} from '@/constants/onboardingChecklist';

/** A topic's authored content, or undefined for an id that has none. */
export const findOnboardingTopic = (topicId) =>
  onboardingWalkthroughs.find((candidate) => candidate.id === topicId);

/**
 * Is this topic offerable? A static-modal topic needs authored slides; a walkthrough's
 * availability rides on content type alone. Lives here rather than in
 * `useOnboardingTopicLaunch.js` so a row can ask without dragging the Controller's import
 * chain in behind it.
 */
export const canLaunchOnboardingTopic = (topicId) => {
  const topic = findOnboardingTopic(topicId);

  if (!topic) {
    return false;
  }

  return onboardingTopicContentTypes[topicId] === 'walkthrough' || Boolean(topic.slides?.length);
};

const titleCase = (slug) =>
  slug
    .split('-')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

/**
 * `categoryId` is read once, not reactively — both call sites (`Tutorials/Panel.vue`,
 * `GetStarted/Widget.vue`) swap views with v-if/v-else, which remounts
 *
 * isAvailable is the same precondition `launchTopic` guards on, so a row can never be
 * enabled for a topic the launcher would refuse to start.
 */
export const useOnboardingTopics = (categoryId) => {
  const category = onboardingCategories.find((candidate) => candidate.id === categoryId);

  const topics = (category?.topicIds ?? []).map((topicId) => {
    const topic = findOnboardingTopic(topicId);
    return {
      id: topicId,
      name: topic?.name ?? titleCase(topicId),
      description: topic?.description ?? '',
      infoText: topic?.infoText ?? '',
      isAvailable: canLaunchOnboardingTopic(topicId),
    };
  });

  return { category, topics };
};
